import { InvitationSchema } from "@storytell/types";
import { RequestBuilder } from "../requestBuilder";
import {
  AuditEventsParamsSchema,
  AuditEventsResponseSchema,
  AuditFacetsResponseSchema,
  CreateOrgInvitationInputSchema,
  GrantOrgMemberProjectsInputSchema,
  OrgDomainParamsSchema,
  OrgEngagementEmailSchema,
  OrgIdParamsSchema,
  OrgInvitationParamsSchema,
  OrgInvitationsResponseSchema,
  OrgMemberAccessResponseSchema,
  OrgMemberParamsSchema,
  OrgMemberProjectParamsSchema,
  OrgMembersResponseSchema,
  OrgProjectMembersResponseSchema,
  OrgProjectParamsSchema,
  OrgProjectsParamsSchema,
  OrgProjectsResponseSchema,
  RevokeOrgMemberProjectResponseSchema,
  SetOrgMemberRoleInputSchema,
  SetOrgMemberRoleResponseSchema,
  UpdateOrgNameInputSchema,
  UpdateOrgNameResponseSchema,
} from "./orgClient.types";

export const UpdateOrgNameRequestConfig = new RequestBuilder({
  paramsSchema: OrgIdParamsSchema,
  path: ({ orgId }) => `/organizations/${orgId}/name`,
  responseSchema: UpdateOrgNameResponseSchema,
  bodySchema: UpdateOrgNameInputSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

export const OrgEngagementEmailRequestConfig = new RequestBuilder({
  paramsSchema: OrgIdParamsSchema,
  path: ({ orgId }) => `/organizations/${orgId}/engagement-email`,
  responseSchema: OrgEngagementEmailSchema,
}).withIncludeOrganizationId();

export const UpdateOrgEngagementEmailRequestConfig = new RequestBuilder({
  paramsSchema: OrgIdParamsSchema,
  path: ({ orgId }) => `/organizations/${orgId}/engagement-email`,
  responseSchema: OrgEngagementEmailSchema,
  bodySchema: OrgEngagementEmailSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

export const OrgMembersRequestConfig = new RequestBuilder({
  paramsSchema: OrgIdParamsSchema,
  path: ({ orgId }) => `/organizations/${orgId}/members`,
  responseSchema: OrgMembersResponseSchema,
}).withIncludeOrganizationId();

export const OrgMemberAccessRequestConfig = new RequestBuilder({
  paramsSchema: OrgMemberParamsSchema,
  path: ({ orgId, userId }) => `/organizations/${orgId}/members/${userId}/access`,
  responseSchema: OrgMemberAccessResponseSchema,
}).withIncludeOrganizationId();

export const SetOrgMemberRoleRequestConfig = new RequestBuilder({
  paramsSchema: OrgMemberParamsSchema,
  path: ({ orgId, userId }) => `/organizations/${orgId}/members/${userId}/role`,
  responseSchema: SetOrgMemberRoleResponseSchema,
  bodySchema: SetOrgMemberRoleInputSchema,
})
  .withMethod("PUT")
  .withIncludeOrganizationId();

export const RevokeOrgMemberRequestConfig = new RequestBuilder({
  paramsSchema: OrgMemberParamsSchema,
  path: ({ orgId, userId }) => `/organizations/${orgId}/members/${userId}`,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeOrganizationId();

export const OrgProjectsRequestConfig = new RequestBuilder({
  paramsSchema: OrgProjectsParamsSchema,
  path: ({ orgId }) => `/organizations/${orgId}/projects`,
  responseSchema: OrgProjectsResponseSchema,
})
  .withQueryParams((params) => {
    const query: Record<string, string> = {};
    if (params.limit !== undefined) query.limit = String(params.limit);
    if (params.afterId !== undefined) query.after_id = params.afterId;
    if (params.q !== undefined) query.q = params.q;
    return query;
  })
  .withIncludeOrganizationId();

export const OrgProjectMembersRequestConfig = new RequestBuilder({
  paramsSchema: OrgProjectParamsSchema,
  path: ({ orgId, projectId }) => `/organizations/${orgId}/projects/${projectId}/members`,
  responseSchema: OrgProjectMembersResponseSchema,
}).withIncludeOrganizationId();

export const GrantOrgMemberProjectsRequestConfig = new RequestBuilder({
  paramsSchema: OrgMemberParamsSchema,
  path: ({ orgId, userId }) => `/organizations/${orgId}/members/${userId}/projects`,
  responseSchema: OrgMemberAccessResponseSchema,
  bodySchema: GrantOrgMemberProjectsInputSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

export const RevokeOrgMemberProjectRequestConfig = new RequestBuilder({
  paramsSchema: OrgMemberProjectParamsSchema,
  path: ({ orgId, userId, projectId }) => `/organizations/${orgId}/members/${userId}/projects/${projectId}`,
  responseSchema: RevokeOrgMemberProjectResponseSchema,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeOrganizationId();

export const OrgInvitationsRequestConfig = new RequestBuilder({
  paramsSchema: OrgIdParamsSchema,
  path: ({ orgId }) => `/organizations/${orgId}/invitations`,
  responseSchema: OrgInvitationsResponseSchema,
}).withIncludeOrganizationId();

export const CreateOrgInvitationRequestConfig = new RequestBuilder({
  paramsSchema: OrgIdParamsSchema,
  path: ({ orgId }) => `/organizations/${orgId}/invitations`,
  responseSchema: InvitationSchema,
  bodySchema: CreateOrgInvitationInputSchema,
})
  .withMethod("POST")
  .withIncludeOrganizationId();

export const DeleteOrgInvitationRequestConfig = new RequestBuilder({
  paramsSchema: OrgInvitationParamsSchema,
  path: ({ orgId, invitationId }) => `/organizations/${orgId}/invitations/${invitationId}`,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeOrganizationId();

/** Withdraws the domain and every grant it produced; a raise written on top of one survives. */
export const RevokeOrgDomainRequestConfig = new RequestBuilder({
  paramsSchema: OrgDomainParamsSchema,
  path: ({ orgId, domainId }) => `/organizations/${orgId}/domains/${domainId}`,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeOrganizationId();

// GET /organizations/{orgId}/audit-events - One keyset page of the org's audit trail
export const OrgAuditEventsRequestConfig = new RequestBuilder({
  paramsSchema: AuditEventsParamsSchema,
  path: ({ orgId }) => `/organizations/${orgId}/audit-events`,
  responseSchema: AuditEventsResponseSchema,
})
  .withQueryParams((params) => {
    const query: Record<string, string> = {};
    if (params.limit !== undefined) query.limit = String(params.limit);
    if (params.afterId !== undefined) query.after_id = params.afterId;
    if (params.area !== undefined) query.area = params.area;
    if (params.actorId !== undefined) query.actorId = params.actorId;
    if (params.category !== undefined) query.category = params.category;
    if (params.resourceType !== undefined) query.resourceType = params.resourceType;
    if (params.resourceId !== undefined) query.resourceId = params.resourceId;
    return query;
  })
  .withIncludeOrganizationId();

// GET /organizations/{orgId}/audit-facets - The filter values the org's trail actually holds
export const OrgAuditFacetsRequestConfig = new RequestBuilder({
  paramsSchema: OrgIdParamsSchema,
  path: ({ orgId }) => `/organizations/${orgId}/audit-facets`,
  responseSchema: AuditFacetsResponseSchema,
}).withIncludeOrganizationId();
